import { inject } from '@angular/core';
import type { ResolveFn, Routes } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { notAuthGuard } from '@core/guards/not-auth.guard';
import { PageLayout } from './core/layout/layout.interface';
import { PageLayoutService } from './core/layout/layout.service';
import { FluxRouteData } from '@core/router/types';
import { featureFlagGuard } from '@core/guards/feature-flag.guard';
import { FeatureFlagsEnum } from '@generated/featureFlags.enum';
import { AuthService } from '@core/services/auth/auth.service';

export const setLayout = (inputLayout: PageLayout): ResolveFn<void> => {
  return () => {
    inject(PageLayoutService).setLayout(inputLayout);
  };
};

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'entrar',
    pathMatch: 'full',
  },
  {
    path: 'entrar',
    resolve: {
      layout: setLayout('blank'),
    },
    canActivate: [notAuthGuard],
    canActivateChild: [notAuthGuard],
    loadChildren: () => import('./pages/sign-in/sign-in.routes').then((m) => m.SignInRoutes),
    data: {
      public: true,
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'redefinir-senha',
    resolve: {
      layout: setLayout('blank'),
    },
    canActivate: [notAuthGuard],
    canActivateChild: [notAuthGuard],
    loadChildren: () =>
      import('./pages/password-reset/password-reset.routes').then((m) => m.PasswordResetRoutes),
    data: {
      public: true,
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'public/form/:hashedId',
    loadComponent: () =>
      import('./pages/public-form/public-form.component').then((m) => m.PublicFormComponent),
    data: {
      public: true,
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'public/stage/form/:hash',
    loadComponent: () =>
      import('./pages/public-link-form/public-link-form.component').then(
        (m) => m.PublicLinksFormComponent,
      ),
    data: {
      public: true,
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'public/card-task/form/:hash',
    loadComponent: () =>
      import('./pages/public-link-form/public-link-form.component').then(
        (m) => m.PublicLinksFormComponent,
      ),
    data: {
      public: true,
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'aceitar-convite',
    resolve: {
      layout: setLayout('blank'),
    },
    canActivate: [notAuthGuard],
    canActivateChild: [notAuthGuard],
    loadChildren: () =>
      import('./pages/public-invite/public-invite.routes').then((m) => m.PublicInviteRoutes),
    data: {
      public: true,
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'fluxos',
    resolve: {
      layout: setLayout('main'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    title: 'Flux - Fluxos de trabalho',
    loadChildren: () => import('./pages/fluxes/fluxes.routes').then((m) => m.FluxesRoutes),
  },
  {
    path: 'fluxo',
    resolve: {
      layout: setLayout('main'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    title: 'Flux - Fluxo de trabalho',
    loadChildren: () => import('./pages/flux/flux.routes').then((m) => m.FluxRoutes),
    data: {
      preloadWhen: {
        matches: /^\/(fluxos|meu-espaco).*/,
      },
    } satisfies FluxRouteData,
  },
  {
    path: 'notificacoes',
    resolve: {
      layout: setLayout('main'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    title: 'Flux - Notificações',
    loadComponent: () =>
      import('./pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
  },
  {
    path: 'gerenciar',
    resolve: {
      layout: setLayout('main'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    title: 'Flux - Fluxo de trabalho',
    loadChildren: () => import('./pages/settings/settings.routes').then((m) => m.SettingsRoutes),
  },

  {
    path: 'perfil',
    resolve: {
      layout: setLayout('main'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    loadChildren: () => import('./pages/profile/profile.routes').then((m) => m.ProfileRoutes),
  },
  {
    path: 'ausencias',
    resolve: {
      layout: setLayout('main'),
    },
    canMatch: [featureFlagGuard],
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    data: {
      flags: [FeatureFlagsEnum.UserDelegations],
    } satisfies FluxRouteData,
    title: 'Ausências',
    loadChildren: () =>
      import('./features/delegations/delegations.routes').then((m) => m.delegationsRoutes),
  },
  {
    path: 'oauth/consent',
    resolve: {
      layout: setLayout('blank'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    title: 'Flux - Autorizar acesso',
    loadChildren: () =>
      import('./features/oauth-consent/oauth-consent.routes').then((m) => m.oauthConsentRoutes),
    data: {
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'meu-espaco',
    resolve: {
      layout: setLayout('main'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    title: 'Flux - Meu espaço',
    loadComponent: () =>
      import('./pages/my-space/my-space.component').then((m) => m.MySpaceComponent),
  },
  {
    path: 'exportar',
    resolve: {
      layout: setLayout('blank'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    title: 'Flux - Exportar',
    loadChildren: () => import('./pages/exports/exports.routes').then((m) => m.ExportsRoutes),
    data: {
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'utils',
    resolve: {
      layout: setLayout('blank'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    loadChildren: () => import('./pages/utils/utils.routes').then((m) => m.UtilsRoutes),
    data: {
      preload: false,
    } satisfies FluxRouteData,
  },
  {
    path: 'not-found',
    loadComponent: () =>
      import('./pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
  {
    path: 'banco-de-dados',
    resolve: {
      layout: setLayout('main'),
    },
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    loadChildren: () =>
      import('./features/databases/database.routes').then((m) => m.databaseRoutes),
  },
  {
    path: 'analytics',
    resolve: {
      layout: setLayout('main'),
    },
    canMatch: [featureFlagGuard],
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    title: 'Analytics',
    data: {
      flags: [FeatureFlagsEnum.FeatureAnalytics],
    } satisfies FluxRouteData,
    loadChildren: () =>
      import('./features/analytics/analytics.routes').then((m) => m.analyticsRoutes),
  },
  {
    path: '**',
    canActivate: [
      () => {
        const auth = inject(AuthService);
        const router = inject(Router);
        const target = auth.isAuthenticated ? '/meu-espaco' : '/entrar';
        return new RedirectCommand(router.createUrlTree([target]));
      },
    ],
    loadComponent: () =>
      import('./pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
];
